import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Loader2, Search, CreditCard, Building2, X, Printer, Clock, CheckCircle2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsultaDeuda = () => {
  const [searchParams] = useSearchParams();
  const [tiposContribuyente, setTiposContribuyente] = useState([]);
  const [tipoSeleccionado, setTipoSeleccionado] = useState("");
  const [idContribuyente, setIdContribuyente] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingPago, setLoadingPago] = useState(false);
  const [deudaData, setDeudaData] = useState(null);
  const [boletasSeleccionadas, setBoletasSeleccionadas] = useState([]);

  // Boletas con pago pendiente/aprobado en pagos_mp (se muestran como "Pendiente Tesorería")
  const [boletasPagadas, setBoletasPagadas] = useState(new Set());
  // Boletas ya acreditadas por Tesorería (se muestran como "Acreditado", siguen bloqueadas)
  const [boletasAcreditadas, setBoletasAcreditadas] = useState(new Set());

  // Estado Modal Nativo
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBoletaUrl, setSelectedBoletaUrl] = useState("");

  // Al montar: cargar tipos y restaurar consulta si venimos de MP (via URL params)
  useEffect(() => {
    fetchTiposContribuyente();
  }, []);

  // Cuando los tipos están cargados y hay params en la URL, restaurar la consulta
  useEffect(() => {
    const tipoParam = searchParams.get("tipo");
    const idParam   = searchParams.get("id");
    if (tipoParam && idParam && tiposContribuyente.length > 0) {
      restaurarDesdeURL(tipoParam, idParam);
    }
  }, [tiposContribuyente]);

  const fetchTiposContribuyente = async () => {
    try {
      const response = await axios.get(`${API}/tipos-contribuyente`);
      setTiposContribuyente(response.data);
    } catch (error) {
      console.error("Error al cargar tipos:", error);
    }
  };

  // Restaura la consulta usando parámetros pasados por URL desde las páginas de resultado de MP
  const restaurarDesdeURL = async (tipo, id) => {
    try {
      setTipoSeleccionado(tipo);
      setIdContribuyente(id);
      const response = await axios.get(`${API}/deuda/${tipo}/${id}`);
      setDeudaData(response.data);
      setBoletasSeleccionadas([]);
      await cargarBoletasPagadas(response.data.boletas);
    } catch (error) {
      console.error("Error al restaurar consulta:", error);
    }
  };

  // Consulta pagos_mp para detectar boletas pagadas/pendientes/acreditadas
  const cargarBoletasPagadas = async (boletas) => {
    try {
      const numeros = boletas.map((b) => String(b.numero));
      const response = await axios.post(`${API}/pago/boletas-pagadas`, { boletas: numeros });
      // El backend devuelve dos listas separadas:
      //   boletas_pagadas   → estado approved (Pendiente Tesorería)
      //   boletas_acreditadas → estado acreditado (ya procesadas por Tesorería)
      const pagadas    = new Set(response.data.boletas_pagadas    || []);
      const acreditadas = new Set(response.data.boletas_acreditadas || []);
      setBoletasPagadas(pagadas);
      setBoletasAcreditadas(acreditadas);
    } catch (error) {
      // Si el endpoint no responde, simplemente no bloquear nada
      setBoletasPagadas(new Set());
      setBoletasAcreditadas(new Set());
    }
  };

  const handleConsultar = async () => {
    if (!tipoSeleccionado || !idContribuyente) {
      toast.error("Por favor, complete todos los campos");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/deuda/${tipoSeleccionado}/${idContribuyente}`);
      setDeudaData(response.data);
      setBoletasSeleccionadas([]);
      await cargarBoletasPagadas(response.data.boletas);
      toast.success("Deuda consultada exitosamente");
    } catch (error) {
      setDeudaData(null);
      toast.error("Error al consultar la deuda");
    } finally {
      setLoading(false);
    }
  };

  const getPlaceholder = (tipo) => {
    const t = String(tipo || "").toUpperCase().trim();
    return t === "IM" ? "Ej: IM202312340000000" : t === "CO" ? "Ej: CO999" : "Ingrese ID";
  };

  const isPlanEspecial = (codigoRaw) => {
    const c = String(codigoRaw || "").toUpperCase().trim();
    return c === "A" || c === "1" || c === "2";
  };

  // ─── Helpers de período ──────────────────────────────────────────────────────
  // Extrae el año de un período: "202601" → "2026", "2026" → "2026"
  const anioDeperiodo = (periodo) => String(periodo || "").substring(0, 4);

  // Extrae el bimestre de un período B: "202601" → "01", "202604" → "04"
  const bimestreDeperiodo = (periodo) => String(periodo || "").substring(4, 6);

  // Determina el semestre al que pertenece un bimestre: "01"-"03" → "1", "04"-"06" → "2"
  const semestreDeBimestre = (bimestre) => {
    const n = parseInt(bimestre, 10);
    if (n >= 1 && n <= 3) return "1";
    if (n >= 4 && n <= 6) return "2";
    return null;
  };

  // Boletas bloqueadas: incluye tanto las pendientes como las ya acreditadas
  const esBloqueada   = (boleta) => boletasPagadas.has(String(boleta.numero)) || boletasAcreditadas.has(String(boleta.numero));
  const esAcreditada  = (boleta) => boletasAcreditadas.has(String(boleta.numero));

  // Calcula el conjunto de números de boleta que NO pueden seleccionarse
  // dado el estado actual de boletasSeleccionadas y los pagos pendientes en pagos_mp.
  const calcularIncompatibles = (seleccionadas, boletas) => {
    const incompatibles = new Set();

    // ── CASO ESPECIAL: bloqueo preventivo por pagos pendientes (bidireccional) ───
    // Previene pagos duplicados cuando el sistema de gestión sincroniza boletas
    // sin conocer los pagos registrados en pagos_mp (pendientes de acreditación).
    //
    // DIRECCIÓN 1 — Una boleta B pagada/pendiente bloquea las grupales que la contienen:
    //   · B del 1er semestre pagada → bloquea Anual y 1er Sem
    //   · B del 2do semestre pagada → bloquea Anual y 2do Sem
    //
    // DIRECCIÓN 2 — Una boleta grupal pagada/pendiente bloquea todo lo que cubre:
    //   · Anual pagada   → bloquea 1er Sem, 2do Sem y todas las B del año
    //   · 1er Sem pagado → bloquea Anual y todas las B del 1er semestre
    //   · 2do Sem pagado → bloquea Anual y todas las B del 2do semestre

    boletas.forEach((b) => {
      const tipo = String(b.tipo_boleta || "").toUpperCase().trim();
      const anio = anioDeperiodo(b.periodo);

      // ── DIRECCIÓN 1: B pagada → bloquea grupales que la contienen ──────────────
      if (tipo === "B" && (boletasPagadas.has(String(b.numero)) || boletasAcreditadas.has(String(b.numero)))) {
        const semB = semestreDeBimestre(bimestreDeperiodo(b.periodo));
        boletas.forEach((bx) => {
          const tipoBx = String(bx.tipo_boleta || "").toUpperCase().trim();
          if (anioDeperiodo(bx.periodo) !== anio) return;
          // Bloquea siempre la Anual del año
          if (tipoBx === "A") incompatibles.add(bx.numero);
          // Bloquea la semestral del semestre al que pertenece esta B
          if (semB && tipoBx === semB) incompatibles.add(bx.numero);
        });
      }

      // ── DIRECCIÓN 2: grupal pagada → bloquea todo lo que cubre ─────────────────
      if ((tipo === "A" || tipo === "1" || tipo === "2") && (boletasPagadas.has(String(b.numero)) || boletasAcreditadas.has(String(b.numero)))) {
        boletas.forEach((bx) => {
          if (bx.numero === b.numero) return; // no bloquearse a sí misma
          const tipoBx = String(bx.tipo_boleta || "").toUpperCase().trim();
          if (anioDeperiodo(bx.periodo) !== anio) return;

          if (tipo === "A") {
            // Anual pagada → bloquea semestrales y todas las B del año
            if (tipoBx === "1" || tipoBx === "2" || tipoBx === "B") {
              incompatibles.add(bx.numero);
            }
          } else if (tipo === "1") {
            // 1er Sem pagado → bloquea Anual y B del 1er semestre
            if (tipoBx === "A") incompatibles.add(bx.numero);
            if (tipoBx === "B" && semestreDeBimestre(bimestreDeperiodo(bx.periodo)) === "1") {
              incompatibles.add(bx.numero);
            }
          } else if (tipo === "2") {
            // 2do Sem pagado → bloquea Anual y B del 2do semestre
            if (tipoBx === "A") incompatibles.add(bx.numero);
            if (tipoBx === "B" && semestreDeBimestre(bimestreDeperiodo(bx.periodo)) === "2") {
              incompatibles.add(bx.numero);
            }
          }
        });
      }
    });
    // ── FIN CASO ESPECIAL ────────────────────────────────────────────────────────

    // ── CASO NORMAL: incompatibilidades por selección activa del usuario ─────────
    // Una boleta grupal seleccionada bloquea las individuales que cubre, y viceversa.
    seleccionadas.forEach((numSel) => {
      const boletaSel = boletas.find((b) => b.numero === numSel);
      if (!boletaSel) return;

      const tipo   = String(boletaSel.tipo_boleta || "").toUpperCase().trim();
      const anio   = anioDeperiodo(boletaSel.periodo);
      const bim    = bimestreDeperiodo(boletaSel.periodo);
      const semSel = semestreDeBimestre(bim);

      boletas.forEach((b) => {
        if (b.numero === numSel) return; // no bloquearse a sí misma
        const tipoB = String(b.tipo_boleta || "").toUpperCase().trim();
        const anioB = anioDeperiodo(b.periodo);
        const bimB  = bimestreDeperiodo(b.periodo);
        const semB  = semestreDeBimestre(bimB);

        if (anioB !== anio) return; // distinto año → no hay conflicto

        if (tipo === "A") {
          // Anual seleccionada → bloquea semestrales y todas las B del año
          if (tipoB === "1" || tipoB === "2" || tipoB === "B") {
            incompatibles.add(b.numero);
          }
        } else if (tipo === "1") {
          // 1er semestre seleccionado → bloquea Anual, 2do semestre y B de bimestres 01-03
          if (tipoB === "A") incompatibles.add(b.numero);
          if (tipoB === "2") incompatibles.add(b.numero);
          if (tipoB === "B" && semB === "1") incompatibles.add(b.numero);
        } else if (tipo === "2") {
          // 2do semestre seleccionado → bloquea Anual, 1er semestre y B de bimestres 04-06
          if (tipoB === "A") incompatibles.add(b.numero);
          if (tipoB === "1") incompatibles.add(b.numero);
          if (tipoB === "B" && semB === "2") incompatibles.add(b.numero);
        } else if (tipo === "B") {
          // Boleta B seleccionada → bloquea Anual
          if (tipoB === "A") incompatibles.add(b.numero);
          // También bloquea el semestre que corresponde a su bimestre
          if (semSel && tipoB === semSel) incompatibles.add(b.numero);
        }
      });
    });
    // ── FIN CASO NORMAL ──────────────────────────────────────────────────────────

    return incompatibles;
  };

  // Verifica si una boleta está deshabilitada para selección
  // (bloqueada por pago ya registrado O incompatible con la selección actual)
  const esDeshabilitada = (boleta, seleccionadas) => {
    if (esBloqueada(boleta)) return true;
    const incompatibles = calcularIncompatibles(seleccionadas, deudaData?.boletas || []);
    return incompatibles.has(boleta.numero);
  };

  const handleCheckboxChange = (boleta) => {
    if (esDeshabilitada(boleta, boletasSeleccionadas)) return;

    if (boletasSeleccionadas.includes(boleta.numero)) {
      // Deseleccionar
      setBoletasSeleccionadas(boletasSeleccionadas.filter((n) => n !== boleta.numero));
    } else {
      // Seleccionar — la validación de incompatibilidad ya se hizo arriba
      setBoletasSeleccionadas([...boletasSeleccionadas, boleta.numero]);
    }
  };

  const handleSelectAll = () => {
    // Selecciona todas las boletas B disponibles (no bloqueadas por pago ni incompatibles)
    // Solo aplica sobre tipo B para no generar conflictos con planes especiales
    const boletasB = deudaData?.boletas?.filter(
      (b) => String(b.tipo_boleta).trim() === "B" && !esBloqueada(b)
    ) || [];
    const allSelected =
      boletasB.length > 0 && boletasB.every((b) => boletasSeleccionadas.includes(b.numero));

    if (allSelected) {
      // Deseleccionar todas las B
      setBoletasSeleccionadas(boletasSeleccionadas.filter(
        (n) => !boletasB.some((b) => b.numero === n)
      ));
    } else {
      // Seleccionar todas las B disponibles (que no generen incompatibilidad con lo ya seleccionado)
      // Primero limpiamos planes especiales de la selección y agregamos todas las B
      const sinPlanes = boletasSeleccionadas.filter((n) => {
        const b = deudaData?.boletas?.find((x) => x.numero === n);
        return b && String(b.tipo_boleta).trim() === "B";
      });
      const todasB = boletasB.map((b) => b.numero);
      setBoletasSeleccionadas([...new Set([...sinPlanes, ...todasB])]);
    }
  };

  const handlePagar = async () => {
    if (!deudaData || boletasSeleccionadas.length === 0) return;
    setLoadingPago(true);
    try {
        const boletasParaPagar = deudaData.boletas
        .filter((b) => boletasSeleccionadas.includes(b.numero))
        .map((b) => ({ numero: String(b.numero), descripcion: `Período ${b.periodo}`, total: parseFloat(b.total || 0) }));

      const response = await axios.post(`${API}/pago/crear-preferencia`, {
        nomencla: deudaData.contribuyente.id_contribuyente,
        contribuyente_nombre: deudaData.contribuyente.nombre,
        boletas: boletasParaPagar
      });
      window.location.href = response.data.init_point;
    } catch (error) {
      toast.error("Error al conectar con Mercado Pago.");
    } finally {
      setLoadingPago(false);
    }
  };

  const handleVerBoleta = (numero) => {
    setSelectedBoletaUrl(`/boleta/${numero}`);
    setIsModalOpen(true);
  };

  const formatCurrency = (amount) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(amount);
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "1900-01-01" || dateStr === "0000-00-00") return "-";
    // Se evita usar new Date() para no perder un día por el offset UTC-3 de Argentina.
    // MySQL devuelve fechas como "2026-05-12" (string ISO sin hora); pasarlas por
    // new Date() las interpreta como UTC medianoche y al localizar resta 3 horas → día anterior.
    const partes = String(dateStr).substring(0, 10).split("-");
    if (partes.length !== 3) return "-";
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
  };

  const renderTipoBadge = (codigoRaw) => {
    const c = String(codigoRaw || "").toUpperCase().trim();
    const badges = {
      'A': <span className="px-2 py-1 bg-green-100 text-green-800 rounded border border-green-200 font-bold text-xs shadow-sm">Anual</span>,
      '1': <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded border border-orange-200 font-bold text-xs shadow-sm">1er Sem</span>,
      '2': <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded border border-orange-200 font-bold text-xs shadow-sm">2do Sem</span>,
      'B': <span className="font-semibold text-slate-600">Boleta</span>,
      'C': <span className="px-2 py-1 bg-red-100 text-red-800 rounded border border-red-200 font-bold text-xs shadow-sm">Certif. Ejec.</span>,
      'P': <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded border border-purple-200 font-bold text-xs shadow-sm">Plan Pago</span>
    };
    return badges[c] || <span className="font-semibold text-slate-400">{c || "Otro"}</span>;
  };

  // Badge según el estado del pago en pagos_mp
  const renderBadgePagada = (boleta) => {
    if (esAcreditada(boleta)) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-200 font-bold text-xs shadow-sm">
          <CheckCircle2 className="w-3 h-3" /> Acreditado
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded border border-amber-200 font-bold text-xs shadow-sm">
        <Clock className="w-3 h-3" /> Pendiente Tesorería
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 relative">
      <Toaster position="top-right" richColors />

      <div className="max-w-6xl mx-auto px-4 pt-6">

        {/* BLOQUE SUPERIOR: Header + Buscador */}
        <div className="shadow-xl rounded-2xl overflow-hidden border border-blue-100 mb-8 bg-white">
          <div className="bg-gradient-to-r from-blue-800 via-blue-700 to-sky-600 text-white px-6 py-3">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/30">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-xl font-bold tracking-tight">Resumen de Deuda</h1>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label className="text-base font-semibold text-gray-700 mb-2 block">Tipo Contribuyente</Label>
                <Select value={tipoSeleccionado} onValueChange={(val) => {
                  setTipoSeleccionado(val);
                  // Precargar el prefijo correspondiente en el campo ID
                  const prefijos = { "IM": "IM", "CO": "CO" };
                  const prefijo = prefijos[val] || "";
                  setIdContribuyente(prefijo);
                }}>
                  <SelectTrigger className="h-12 border-2 border-gray-200 focus:border-blue-500">
                    <SelectValue placeholder="Seleccione tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposContribuyente.map((t) => <SelectItem key={t.codigo} value={t.codigo}>{t.descripcion}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-base font-semibold text-gray-700 mb-2 block">ID Contribuyente</Label>
                <Input
                  type="text" placeholder={getPlaceholder(tipoSeleccionado)}
                  value={idContribuyente} onChange={(e) => setIdContribuyente(e.target.value.toUpperCase())}
                  className="h-12 border-2 border-gray-200 focus:border-blue-500 uppercase"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleConsultar} disabled={loading} className="h-12 w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 text-white font-semibold shadow-lg">
                  {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Consultando...</> : <><Search className="mr-2 h-5 w-5" />Consultar</>}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {deudaData && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-blue-100">
                <div className="bg-gradient-to-r from-blue-700 to-sky-600 p-6 text-white">
                  <h2 className="text-xl font-bold mb-2">{deudaData.contribuyente.nombre}</h2>
                  <p className="text-blue-100 text-sm"><span className="font-semibold">Domicilio:</span> {deudaData.contribuyente.direccion_real || "No registra"}</p>
                  <p className="text-blue-100 text-sm"><span className="font-semibold">Nomenclatura:</span> {deudaData.contribuyente.id_contribuyente}</p>
                </div>

                {/* Aviso si hay boletas bloqueadas */}
                {(boletasPagadas.size > 0 || boletasAcreditadas.size > 0) && (
                  <div className="flex items-center gap-3 px-6 py-3 bg-amber-50 border-b border-amber-200">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-800 font-medium">
                      {(() => {
                        const total = boletasPagadas.size + boletasAcreditadas.size;
                        return total === 1
                          ? "1 boleta tiene un pago registrado pendiente de acreditación o ya acreditado. Las boletas Anual/Semestral que incluyan ese período están deshabilitadas para evitar pagos duplicados."
                          : `${total} boletas tienen pagos registrados (pendientes o acreditados). Las boletas Anual/Semestral que incluyan esos períodos están deshabilitadas para evitar pagos duplicados.`;
                      })()}
                    </p>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 border-b-2 border-slate-200">
                      <tr>
                        <th className="px-3 py-4 text-center w-12">
                          <Checkbox
                            checked={
                              deudaData?.boletas?.filter(
                                b => String(b.tipo_boleta).trim() === 'B' && !esBloqueada(b)
                              ).length > 0 &&
                              deudaData?.boletas?.filter(
                                b => String(b.tipo_boleta).trim() === 'B' && !esBloqueada(b)
                              ).every(b => boletasSeleccionadas.includes(b.numero))
                            }
                            onCheckedChange={handleSelectAll}
                          />
                        </th>
                        <th className="px-3 py-4 text-left text-xs font-bold text-slate-600 uppercase">Tipo</th>
                        <th className="px-3 py-4 text-left text-xs font-bold text-slate-600 uppercase">Número</th>
                        <th className="px-3 py-4 text-left text-xs font-bold text-slate-600 uppercase">Período</th>
                        <th className="px-3 py-4 text-right text-xs font-bold text-slate-600 uppercase">Imp. Nominal</th>
                        <th className="px-3 py-4 text-right text-xs font-bold text-slate-600 uppercase">Fecha Vto</th>
                        <th className="px-3 py-4 text-right text-xs font-bold text-slate-600 uppercase">Interés</th>
                        <th className="px-3 py-4 text-right text-xs font-bold text-slate-600 uppercase">% Dto.</th>
                        <th className="px-3 py-4 text-right text-xs font-bold text-slate-600 uppercase">Dto.</th>
                        <th className="px-3 py-4 text-right text-xs font-bold text-slate-600 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {deudaData.boletas.map((boleta) => {
                        const pagada        = esBloqueada(boleta);
                        const incompatible  = !pagada && esDeshabilitada(boleta, boletasSeleccionadas);
                        const deshabilitada = pagada || incompatible;
                        const seleccionada  = boletasSeleccionadas.includes(boleta.numero);
                        return (
                          <tr
                            key={boleta.numero}
                            className={`${
                              pagada
                                ? "bg-amber-50 opacity-75"
                                : incompatible
                                ? "bg-slate-50 opacity-50"
                                : seleccionada
                                ? "bg-blue-50"
                                : "hover:bg-slate-50"
                            }`}
                          >
                            <td className="px-3 py-3 text-center">
                              <Checkbox
                                checked={seleccionada}
                                onCheckedChange={() => handleCheckboxChange(boleta)}
                                disabled={deshabilitada}
                              />
                            </td>
                            <td className="px-3 py-3 text-sm">
                              {pagada ? renderBadgePagada(boleta) : renderTipoBadge(boleta.tipo_boleta)}
                            </td>
                            <td className="px-3 py-3 text-sm font-bold">
                              <button
                                onClick={() => handleVerBoleta(boleta.numero)}
                                className={`hover:underline ${pagada ? "text-amber-600 hover:text-amber-800" : "text-blue-600 hover:text-blue-800"}`}
                              >
                                {boleta.numero}
                              </button>
                            </td>
                            <td className="px-3 py-3 text-sm font-medium text-slate-800">{boleta.periodo}</td>
                            <td className="px-3 py-3 text-sm text-right text-slate-600">{formatCurrency(boleta.importe_nominal)}</td>
                            <td className="px-3 py-3 text-sm text-right text-slate-500">{formatDate(boleta.fecha_vto)}</td>
                            <td className="px-3 py-3 text-sm text-right text-red-600">{formatCurrency(boleta.interes)}</td>
                            <td className="px-3 py-3 text-sm text-right text-green-700 font-bold">
                              {boleta.importe_nominal > 0 ? Math.round((boleta.porc_dto / boleta.importe_nominal) * 100) : 0}%
                            </td>
                            <td className="px-3 py-3 text-sm text-right text-green-600">{formatCurrency(parseFloat(boleta.porc_dto || 0) + parseFloat(boleta.dto || 0))}</td>
                            <td className="px-3 py-3 text-sm text-right font-bold text-slate-900">{formatCurrency(boleta.total)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* LATERAL DE TOTALES */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">Deuda Total</h3>
                <p className="text-3xl font-bold text-slate-800">
                  {formatCurrency(deudaData.boletas.filter(b => !isPlanEspecial(b.tipo_boleta) && !esBloqueada(b)).reduce((acc, b) => acc + parseFloat(b.total), 0))}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-600 to-sky-500 rounded-2xl shadow-xl p-6 border-2 border-blue-400 text-white">
                <h3 className="text-sm font-semibold text-blue-100 uppercase mb-2">Total a pagar</h3>
                <p className="text-3xl font-bold text-white">
                  {formatCurrency(deudaData.boletas.filter(b => boletasSeleccionadas.includes(b.numero)).reduce((acc, b) => acc + parseFloat(b.total), 0))}
                </p>
              </div>
              <Button
                onClick={handlePagar}
                disabled={boletasSeleccionadas.length === 0 || loadingPago}
                className="w-full h-14 bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white font-semibold shadow-lg"
              >
                {loadingPago
                  ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Procesando...</>
                  : <><CreditCard className="mr-2 h-5 w-5" />Pagar con Mercado Pago</>
                }
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL BOLETA */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-5xl h-[92vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-5 border-b bg-slate-50">
              <h3 className="font-bold text-blue-900 flex items-center gap-2 text-xl">
                <Printer className="w-6 h-6" /> Boleta #{selectedBoletaUrl.split('/').pop()}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                <X className="w-8 h-8 text-slate-400" />
              </button>
            </div>
            <div className="flex-1">
              <iframe src={selectedBoletaUrl} className="w-full h-full border-none" title="Boleta" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultaDeuda;
